import { useState, useEffect, useCallback } from "react"
import TopNavBar from "./TopNavBar"
import { projectURL } from "./utils/mockdata"
import { Eye, EyeOff, User, Mail, Phone, Lock, Users, ChevronLeft, ChevronRight, CalendarDays, Waves } from "lucide-react"
import Modal from "./Modal"
import MultiSelectHobbiest from "./MultiSelecHobbiest"

const ViewProfile = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [selectedIcon, setSelectedIcon] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMessage, setModalMessage] = useState("")
  const [modalTitle, setModalTitle] = useState("")
  const [modalType, setModalType] = useState("info")
  const [icons, setIcons] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const loggedUserId = sessionStorage.getItem("loggedUserId")

  const [userProfile, setUserProfile] = useState({
    email: "",
    username: "",
    password: "",
    gender: "",
    imagePath: "",
    mobile: "",
    DOB: "",
    Hobbies: [],
  })

  const toggleVisibility = () => setIsVisible((prevState) => !prevState)

  const [currentSliderIndex, setCurrentSliderIndex] = useState(0)
  const [sliderRef, setSliderRef] = useState(null)

  const scrollToIndex = (index) => {
    if (sliderRef) {
      const itemWidth = 80 // 64px width + 16px margin
      sliderRef.scrollTo({
        left: index * itemWidth,
        behavior: "smooth"
      })
      setCurrentSliderIndex(index)
    }
  }

  const handlePrevious = () => {
    const newIndex = currentSliderIndex > 0 ? currentSliderIndex - 1 : icons.length - 1
    scrollToIndex(newIndex)
  }

  const handleNext = () => {
    const newIndex = currentSliderIndex < icons.length - 1 ? currentSliderIndex + 1 : 0
    scrollToIndex(newIndex)
  }

  const handleIconSelectWithIndex = (iconUrl, index) => {
    setSelectedIcon(iconUrl)
    setUserProfile((prev) => ({ ...prev, imagePath: iconUrl }))
    setCurrentSliderIndex(index)
  }

  // Fetch profile data
  const fetchUserProfile = useCallback(async () => {
    if (!loggedUserId) {
      setModalTitle("Authentication Error")
      setModalMessage("Please log in to view your profile.")
      setModalType("error")
      setIsModalOpen(true)
      return
    }

    try {
      const response = await fetch(`${projectURL}profiles/${loggedUserId}`)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const data = await response.json()
      setUserProfile({
        email: data?.email || "",
        username: data?.username || "",
        password: data?.password || "",
        gender: data?.gender || "",
        imagePath: data?.imagePath || "",
        mobile: data?.mobile || "",
        DOB: data?.DOB || "",
        Hobbies: data?.Hobbies || [],
      })
      setSelectedIcon(data?.imagePath || "")
    } catch (error) {
      console.error("Error fetching profile:", error)
      setModalTitle("Error")
      setModalMessage("Failed to load profile data. Please try again.")
      setModalType("error")
      setIsModalOpen(true)
    }
  }, [loggedUserId])

  // Fetch icons
  const fetchIcons = useCallback(async () => {
    try {
      const response = await fetch(`${projectURL}dashboard/listpics`)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const data = await response.json()
      setIcons(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error("Error fetching icons:", error)
      setIcons([])
    }
  }, [])

  const validateForm = () => {
    const { username, email, password, mobile } = userProfile

    if (!username?.trim()) {
      return { isValid: false, message: "Username is required." }
    }

    if (!email?.trim()) {
      return { isValid: false, message: "Email is required." }
    }

    if (!password?.trim()) {
      return { isValid: false, message: "Password is required." }
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      return { isValid: false, message: "Please enter a valid email address." }
    }

    // Mobile validation (if provided)
    if (mobile && mobile.trim()) {
      const mobileRegex = /^[0-9]{10,15}$/
      if (!mobileRegex.test(mobile.replace(/\s+/g, ""))) {
        return { isValid: false, message: "Please enter a valid mobile number." }
      }
    }

    return { isValid: true }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const validation = validateForm()
    if (!validation.isValid) {
      setModalTitle("Validation Error")
      setModalMessage(validation.message)
      setModalType("error")
      setIsModalOpen(true)
      return
    }

    const finalIcon = selectedIcon || userProfile.imagePath
    setIsSubmitting(true)

    try {
      sessionStorage.setItem("loggedUserName", userProfile.username)
      sessionStorage.setItem("loggedInUserImage", finalIcon)

      const response = await fetch(`${projectURL}profiles/update/${loggedUserId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...userProfile, imagePath: finalIcon }),
      })

      if (response.ok) {
        setModalTitle("Success")
        setModalMessage("Profile updated successfully!")
        setModalType("success")
        setIsModalOpen(true)
        await fetchUserProfile()
        setTimeout(() => {
          setIsModalOpen(false)
        }, 3000)
      } else {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || "Failed to update profile")
      }
    } catch (error) {
      console.error("Error updating profile:", error)
      setModalTitle("Update Failed")
      setModalMessage(error.message || "Failed to update profile. Please try again.")
      setModalType("error")
      setIsModalOpen(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    fetchUserProfile()
    fetchIcons()
  }, [fetchUserProfile, fetchIcons])


  const sliderStyles = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  `

  return (
    <>
      <style>{sliderStyles}</style>
      <div className="min-h-screen bg-gray-50">
        <TopNavBar />
        <main className="py-2">
          <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
            <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
              {/* Header Section */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-1">
                <div className="text-center">
                  <h1 className="text-lg font-bold text-white">Profile Settings</h1>
                  <p className="text-blue-100 mt-1">
                    Hey <span className="font-bold">{userProfile.username || "User"}</span>, update your profile
                    information
                  </p>
                </div>
              </div>

              {/* Form Section */}
              <div className="px-4 py-4">
                <form className="space-y-4">
                  {/* Profile Picture Selection */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Choose Profile Picture</label>
                    <div className="bg-gray-50 rounded-xl p-6">
                      {icons.length > 0 ? (
                        <div className="relative">
                          {/* Navigation Arrows */}
                          <button
                            type="button"
                            onClick={handlePrevious}
                            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-200 hover:bg-blue-50 border border-gray-200"
                            aria-label="Previous image"
                          >
                            <ChevronLeft className="w-5 h-5 text-gray-600 hover:text-blue-600" />
                          </button>

                          <button
                            type="button"
                            onClick={handleNext}
                            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-200 hover:bg-blue-50 border border-gray-200"
                            aria-label="Next image"
                          >
                            <ChevronRight className="w-5 h-5 text-gray-600 hover:text-blue-600" />
                          </button>

                          {/* Image Slider */}
                          <div
                            ref={setSliderRef}
                            className="flex overflow-x-auto space-x-4 pb-2 px-8 scrollbar-hide scroll-smooth"
                            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                          >
                            {icons.map((icon, index) => (
                              <div key={icon.id} className="flex-shrink-0">
                                <img
                                  src={icon.icon || "/placeholder.svg"}
                                  alt={`Profile option ${icon.id}`}
                                  className={`w-16 h-16 rounded-full cursor-pointer transition-all duration-300 border-3 hover:scale-110 hover:shadow-xl
                                    ${selectedIcon === icon.icon
                                      ? "border-green-500 shadow-lg ring-2 ring-green-200 scale-105"
                                      : "border-blue-400 hover:border-blue-500"
                                    }`}
                                  onClick={() => handleIconSelectWithIndex(icon.icon, index)}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm text-center py-8">No profile pictures available</p>
                      )}
                    </div>
                  </div>

                  {/* Form Fields Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Username */}
                    <div className="space-y-2">
                      <label htmlFor="username" className="block text-sm font-semibold text-gray-700">
                        <User className="inline w-4 h-4 mr-2" />
                        Username
                      </label>
                      <input
                        type="text"
                        id="username"
                        name="username"
                        placeholder="Enter your username"
                        className="w-full px-4 py-1 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        value={userProfile.username}
                        onChange={(e) => setUserProfile({ ...userProfile, username: e.target.value })}
                        required
                      />
                    </div>

                    {/* Gender */}
                    <div className="space-y-2">
                      <label htmlFor="gender" className="block text-sm font-semibold text-gray-700">
                        <Users className="inline w-4 h-4 mr-2" />
                        Gender
                      </label>
                      <select
                        id="gender"
                        name="gender"
                        value={userProfile.gender}
                        onChange={(e) => setUserProfile({ ...userProfile, gender: e.target.value })}
                        className="w-full px-4 py-1 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      >
                        <option value="" disabled>
                          Select Gender
                        </option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700">
                        <Mail className="inline w-4 h-4 mr-2" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="Enter your email address"
                        className="w-full px-4 py-1 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        value={userProfile.email}
                        onChange={(e) => setUserProfile({ ...userProfile, email: e.target.value })}
                        required
                      />
                    </div>

                    {/* Mobile */}
                    <div className="space-y-2">
                      <label htmlFor="mobile" className="block text-sm font-semibold text-gray-700">
                        <Phone className="inline w-4 h-4 mr-2" />
                        Mobile Number
                      </label>
                      <input
                        type="tel"
                        id="mobile"
                        name="mobile"
                        placeholder="Enter your mobile number"
                        className="w-full px-4 py-1 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        value={userProfile.mobile || ""}
                        onChange={(e) => setUserProfile({ ...userProfile, mobile: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="password" className="block text-sm font-semibold text-gray-700">
                        <Lock className="inline w-4 h-4 mr-2" />
                        Password
                      </label>
                      <div className="relative">
                        <input
                          id="password"
                          type={isVisible ? "text" : "password"}
                          placeholder="Enter your password"
                          className="w-full px-4 py-1 pr-12 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                          value={userProfile.password}
                          onChange={(e) => setUserProfile({ ...userProfile, password: e.target.value })}
                          required
                        />
                        <button
                          type="button"
                          onClick={toggleVisibility}
                          className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-blue-600 focus:outline-none transition-colors duration-200"
                          aria-label={isVisible ? "Hide password" : "Show password"}
                        >
                          {isVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>


                    {/* DOB */}
                    <div className="space-y-2">
                      <label htmlFor="DOB" className="block text-sm font-semibold text-gray-700">
                        <CalendarDays className="inline w-4 h-4 mr-2" />
                        Date Of Birth
                      </label>
                      <div className="relative">
                        <input
                          id="DOB"
                          type={"date"}
                          placeholder="Enter your DOB"
                          className="w-full px-4 py-1 pr-12 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                          value={
                            userProfile.DOB
                              ? new Date(userProfile.DOB).toISOString().split("T")[0]
                              : ""
                          }
                          onChange={(e) => setUserProfile({ ...userProfile, DOB: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Hobbies */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      <Waves className="inline w-4 h-4 mr-2" /> Hobbies
                    </label>
                    <MultiSelectHobbiest value={userProfile.Hobbies} onChange={(selected) => setUserProfile({ ...userProfile, Hobbies: selected })} />
                  </div>


                  {/* Submit Button */}
                  <div className="pt-4">
                    <button
                      onClick={handleSubmit}
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full py-3 px-6 rounded-lg font-semibold text-white shadow-lg transition-all duration-200 ${isSubmitting
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 hover:shadow-xl transform hover:-translate-y-0.5"
                        }`}
                    >
                      {isSubmitting ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Updating Profile...
                        </div>
                      ) : (
                        "Update Profile"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </main>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalTitle}
        message={modalMessage}
        type={modalType}
      />
    </>
  )
}

export default ViewProfile
